import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; stab: readonly string[]; pad: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Am: { root: 'A1', stab: ['C4', 'E4', 'G4', 'B4'], pad: ['E3', 'B3', 'C4', 'G4'] },
  F: { root: 'F1', stab: ['C4', 'E4', 'G4', 'A4'], pad: ['F3', 'C4', 'E4', 'A4'] },
  Dm: { root: 'D2', stab: ['C4', 'E4', 'F4', 'A4'], pad: ['D3', 'A3', 'C4', 'F4'] },
  Esus: { root: 'E2', stab: ['B3', 'D4', 'E4', 'A4'], pad: ['E3', 'B3', 'D4', 'A4'] },
  C: { root: 'C2', stab: ['B3', 'D4', 'E4', 'G4'], pad: ['C3', 'G3', 'B3', 'E4'] },
  G: { root: 'G1', stab: ['B3', 'D4', 'G4', 'A4'], pad: ['D3', 'B3', 'D4', 'G4'] },
  E7: { root: 'E2', stab: ['B3', 'D4', 'E4', 'G#4'], pad: ['E3', 'B3', 'D4', 'G#4'] },
};

const PROGS = [
  ['Am', 'F', 'Dm', 'Esus'],
  ['Am', 'F', 'C', 'G'],
  ['Am', 'Dm', 'G', 'C'],
  ['Am', 'G', 'F', 'E7'],
] as const;

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'E5', 1, 0.7], [3, 'G5', 2], [6, 'A5', 3], [10, 'G5', 2], [12, 'E5', 2], [14, 'D5', 2]],
  [[0, 'C5', 3], [3, 'E5', 2], [6, 'G5', 4], [10, 'E5', 2], [12, 'C5', 2], [14, 'A4', 2]],
  [[0, 'A4', 2], [2, 'C5', 1, 0.7], [3, 'D5', 2], [6, 'F5', 3], [10, 'E5', 2], [12, 'D5', 2], [14, 'C5', 2]],
  [[0, 'B4', 3], [3, 'D5', 2], [6, 'E5', 6], [12, 'B4', 1, 0.7], [13, 'D5', 1, 0.8], [14, 'E5', 2]],
];

const BASSES: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[2, 0, 1.6], [6, 0, 1.2], [7, 12, 0.8], [10, 0, 1.6], [13, 7, 1], [14, 12, 1.4]],
  [[2, 0, 1.6], [6, 0, 1.2], [10, 0, 1.6], [11, 12, 0.8], [14, 7, 1.4]],
  [[2, 0, 1.2], [3, 12, 0.8], [6, 0, 1.2], [10, 0, 1.2], [11, 12, 0.8], [14, 7, 1.4]],
];

const ARPS = [
  [0, 2, 1, 3, 2, 0, 3, 1, 0, 2, 1, 3, 2, 3, 1, 2],
  [0, 1, 2, 3, 1, 2, 3, 0, 2, 3, 0, 1, 3, 2, 1, 0],
  [0, 2, 3, 1, 0, 2, 3, 2, 0, 2, 3, 1, 3, 2, 1, 2],
] as const;

const KEYS = [0, -2, 2, -3, 3, 1, -1] as const;
const CLAPS = ['....X.......X...', '....X.......X..o'] as const;
const OPEN_HATS = ['..X...X...X...X.', '..X...X...X.x.X.'] as const;
const CLOSED_HATS = ['.o.o.o.o.o.o.o.x', '.o.x.o.o.o.x.o.x'] as const;
const RIMS = ['...x......x..x..', '......x..x....x.', '...x..x....x..x.'] as const;
const SHAKERS = ['x-o-x-o-x-o-x-o-', 'x-o-x-oox-o-x-o-'] as const;
const STABS = ['X..x..x...x..x..', 'X..x..x.x..x..x.', 'x..x..x...x...x.'] as const;
const INTRO_STABS = ['...x......x.....', '......x.......x.'] as const;
const ROLLS = ['X.......X.......', 'X...X...X...X...', 'x.x.x.x.x.x.x.x.', 'xxxxxxxxxxxx....'] as const;
const MINOR = [9, 11, 0, 2, 4, 5, 7] as const;

const PLAN: FormPlan = {
  intro: 8,
  outro: 8,
  body: [
    { name: 'groove', bars: 8, min: 4, rank: 2 },
    { name: 'break', bars: 8, min: 4, rank: 3 },
    { name: 'drop', bars: 16, min: 4, rank: 1 },
    { name: 'groove2', bars: 8, min: 4, rank: 5, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 4 },
  ],
};

export type HouseVariant = {
  swing: number;
  lufs: number;
  mix: (bpm: number) => Score['mix'];
  claps?: readonly string[];
  openHats?: readonly string[];
  closedHats?: readonly string[];
  rims?: readonly string[];
  shakers?: readonly string[];
  stabs?: readonly string[];
  introStabs?: readonly string[];
  breakSub?: string;
};

const BASE: HouseVariant = {
  swing: 54,
  lufs: -10.5,
  mix: (bpm) => ({ reverbDecay: 2.8, delayTime: (60 / bpm) * 0.75, delayFeedback: 0.32, drive: 1.6, softness: 1.3, pumpDepth: 0.55, pumpRelease: 0.3 }),
};

export function houseScore(t: Template, v: HouseVariant, seed: string, p: Params): Score {
  const ch = chooser(t, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const bassRow = ch.pick(BASSES);
  const arpRow = ch.pick(ARPS);
  const clapP = ch.pick(v.claps ?? CLAPS);
  const hatOP = ch.pick(v.openHats ?? OPEN_HATS);
  const hatCP = ch.pick(v.closedHats ?? CLOSED_HATS);
  const rimP = ch.pick(v.rims ?? RIMS);
  const shakerP = ch.pick(v.shakers ?? SHAKERS);
  const stabP = ch.pick(v.stabs ?? STABS);
  const introP = ch.pick(v.introStabs ?? INTRO_STABS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.stab]) }]),
        MINOR,
        [69, 81],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, v.swing, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outro = total - form[form.length - 1]!.bars;
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const groove1 = find(form, 'groove');
  const groove2 = find(form, 'groove2');
  const rng = createRng(`${t.id}-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, t: number, vel: number) => events.push({ v, t, bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes });

  const autos = [
    { v: 'stabLp', points: [[g.at(0), 500], [g.at(brk ?? drop1), 2600]] },
    {
      v: 'padLp',
      points: [[g.at(0), 400], [g.at(intro), 900], ...(brk === undefined ? [[g.at(drop1), 4200]] : [[g.at(brk), 700], [g.at(brk + sizeOf(form, 'break')), 4200]])],
    },
    { v: 'padLp', points: [[g.at(drop1), 1400], [g.at(outro), 1400], [g.at(total), 350]] },
    { v: 'stabLp', points: [[g.at(outro), 2600], [g.at(total), 450]] },
  ] as Score['autos'];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const c = prog[i]!;
    const at = (step: number) => g.at(b.bar, step);
    const inDrop = b.name === 'drop' || b.name === 'drop2';
    const groove = b.name === 'groove' || b.name === 'groove2' || inDrop;
    const outroBody = b.name === 'outro' && !b.last;

    if (b.name !== 'break' && !(b.name === 'outro' && b.last)) {
      for (const h of hits('X...X...X...X...')) hit('kick', b.bar, h.step, at(h.step), h.vel);
    }
    if (groove || outroBody) {
      for (const h of hits(clapP)) hit('clap', b.bar, h.step, at(h.step), human(h.vel));
    }
    if (groove || (b.name === 'intro' && b.index >= b.bars / 2 && e >= 0.4) || outroBody) {
      for (const h of hits(hatOP)) hit('hatO', b.bar, h.step, at(h.step), human(h.vel * 0.9));
    }
    if (groove || outroBody) {
      if (e >= 0.3) for (const h of hits(hatCP)) hit('hatC', b.bar, h.step, at(h.step) + rng.range(-0.002, 0.002), human(h.vel));
      if (e >= 0.45) for (const h of hits(rimP)) hit('rim', b.bar, h.step, at(h.step), human(h.vel));
    }
    if (groove || b.name === 'intro' || outroBody) {
      for (const h of hits(shakerP)) hit('shaker', b.bar, h.step, at(h.step) + rng.range(-0.003, 0.003), human(h.vel));
    }

    if (groove || outroBody) {
      for (const [step, semis, len] of bassRow) {
        const note = tr(transpose(c.root, semis));
        play('bass', b.bar, step, note, len * s * 0.95, human(0.9));
        play('sub', b.bar, step, note, len * s * 0.95, 0.9);
      }
    }
    if (b.name === 'break' && v.breakSub !== undefined) {
      for (const h of hits(v.breakSub)) play('sub', b.bar, h.step, tr(c.root), s * 3.6, 0.6 * h.vel);
    }

    const stab = c.stab.map(tr);
    if (b.name === 'intro') {
      for (const h of hits(introP)) chord('stab', b.bar, h.step, stab, s * 1.6, human(0.8));
    } else if (groove || b.name === 'outro') {
      for (const h of hits(stabP)) chord('stab', b.bar, h.step, stab, s * 1.4, human(h.vel));
    }

    if (b.name === 'intro' || b.name === 'break' || inDrop || b.name === 'outro') {
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.98, b.name === 'break' ? 0.9 : 0.6);
    }

    if ((b.name === 'break' || inDrop || b.name === 'groove2') && e >= 0.5) {
      const notes = stab.map((n) => transpose(n, 12));
      arpRow.forEach((idx, step) => play('arp', b.bar, step, notes[idx]!, s * 0.8, human(step % 2 === 0 ? 0.85 : 0.5)));
    } else if (b.name === 'groove' && e >= 0.85) {
      const notes = stab.map((n) => transpose(n, 12));
      arpRow.forEach((idx, step) => play('arp', b.bar, step, notes[idx]!, s * 0.8, human(step % 2 === 0 ? 0.6 : 0.35)));
    }

    const row = hook[i]!;
    if (b.name === 'break' && b.index >= b.bars / 2) {
      for (const [step, note, len, vel] of row) play('pluck', b.bar, step, tr(note), len * s * 0.95, (vel ?? 0.9) * 0.8);
    }
    if (inDrop) {
      const octave = (b.name === 'drop2' && e >= 0.6) || e >= 0.85;
      for (const [step, note, len, vel] of row) {
        play('lead', b.bar, step, tr(note), len * s * 0.92, human(vel ?? 0.95));
        play('pluck', b.bar, step, tr(transpose(note, -12)), len * s * 0.9, (vel ?? 0.9) * 0.7);
        if (octave) play('lead', b.bar, step, tr(transpose(note, 12)), len * s * 0.9, (vel ?? 0.9) * 0.45);
      }
    }

    const roll = b.index - (b.bars - 4);
    if (b.name === 'break' && roll >= 0) {
      for (const h of hits(ROLLS[roll]!)) hit('snare', b.bar, h.step, at(h.step), 0.35 + 0.6 * (roll / 4 + h.step / 64));
    }
    if (b.name === 'groove' && b.last) {
      for (const h of hits('............x.xx')) hit('snare', b.bar, h.step, at(h.step), 0.7);
    }
  }

  const riseFrom = brk === undefined ? Math.max(0, drop1 - 2) : brk + sizeOf(form, 'break') / 2;
  events.push({ v: 'rise', t: g.at(riseFrom), end: g.at(drop1) - s * 4, bar: riseFrom, step: 0, vel: 1 });
  if (drop2 !== undefined) events.push({ v: 'rise', t: g.at(drop2 - 2), end: g.at(drop2), bar: drop2 - 2, step: 0, vel: 1 });
  for (const bar of [drop1, drop2, groove1]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, g.at(bar), 1);
    hit('impact', bar, 0, g.at(bar), 1);
  }
  if (groove2 !== undefined) {
    events.push({ v: 'fall', t: g.at(groove2), end: g.at(groove2 + 2), bar: groove2, step: 0, vel: 1 });
    hit('crash', groove2, 0, g.at(groove2), 0.8);
  }

  return {
    id: trackId(t, seed, p),
    template: t.id,
    seed,
    params: p,
    title: ch.orig ? t.ref.title : titleOf(seed),
    genre: t.genre,
    bpm: p.bpm,
    swing: v.swing,
    lufs: v.lufs,
    key,
    form,
    mix: v.mix(p.bpm),
    events,
    autos,
    accents: { kick: 'kick', clap: 'snare', stab: 'stab', lead: 'melody', pluck: 'melody', impact: 'drop' },
  };
}

export const HOUSE: Template = {
  id: 'house',
  genre: 'deep/melodic house',
  bpm: [100, 128],
  ref: { seed: 'mare-alta', title: 'Maré Alta', params: { bpm: 124, bars: 64, energy: 0.7 } },
  score: (seed, p) => houseScore(HOUSE, BASE, seed, p),
};
